import * as React from 'react';

import { DescriptionPage_SelfServeConfiguration_AboutModule_SubsectionName as AboutModuleSubsectionName } from '__generated__/graphql-types';

import ArticlesRecommendationsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ArticlesRecommendationsModuleRenderer';
import LearnerIncentiveModuleRenderer from 'bundles/unified-description-page-common/module-renderers/LearnerIncentiveModuleRenderer';
import PageBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/PageBannerModuleRenderer';
import BenefitsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/PortfolioProjectBenefitsModuleRenderer';
import ProjectRecommendationsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ProjectRecommendationsModuleRenderer';
import TestimonialsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/TestimonialsModuleRenderer';
import ThinBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ThinBannerModuleRenderer';
import ConsumerBreadcrumbsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/breadcrumbs/ConsumerBreadcrumbsModuleRenderer';
import ProductOverviewModuleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/ConsumerProjectProductOverviewModuleRenderer';
import ProjectContentModuleRenderer from 'bundles/unified-description-page-common/module-renderers/project-content/PortfolioProjectContentModuleRenderer';
import UpsellModuleRenderer from 'bundles/unified-description-page-common/module-renderers/upsell/ConsumerUpsellModuleRenderer';
import { useXdpSubscriptionsForwardDiscovery } from 'bundles/unified-description-page-common/providers/SubscriptionsForwardDiscoveryDataProvider';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import {
  KeyInformationItemNames,
  ModuleNames,
  ProductDetailItemNames,
  UpsellCardNames,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import type {
  PageConfig,
  ProductOverviewModuleConfig,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import { getAudienceLanguageCode } from 'bundles/unified-description-page-common/utils/dubbingUtil';

import ProjectsHeroImage from 'images/unified-description-page/projects-hero-image.png';

type Props = {
  pageData: ConsumerCoursePageFieldsFragment;
  pageConfig?: PageConfig | null;
};

const Renderer = ({ pageData, pageConfig }: Props) => {
  const { shouldShowXdpChanges } = useXdpSubscriptionsForwardDiscovery();

  if (!pageConfig || !pageConfig.modules.length) {
    return null;
  }

  const modulesToRender = pageConfig.modules.map((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        return (
          <ProductOverviewModuleRenderer
            pageData={pageData}
            config={
              {
                ...moduleConfig,
                // visibility of hero language info depends on XDP product AI dubbing, user site language
                showHeroLanguageInfo:
                  pageData.dubbedLanguages?.length && getAudienceLanguageCode(pageData.dubbedLanguages),
                showBackgroundImage: true,
                backgroundImage: ProjectsHeroImage,
                hideInstructors: true,
                hideCourseraPlus: true,
                keyInfoModule: {
                  items: [
                    { name: KeyInformationItemNames.ProductType },
                    { name: KeyInformationItemNames.Difficulty },
                    { name: KeyInformationItemNames.Duration },
                    { name: KeyInformationItemNames.EarnCertificate },
                  ],
                },
                aboutModule: {
                  subsections: [
                    { name: AboutModuleSubsectionName.LearningOutcomes },
                    { name: AboutModuleSubsectionName.Skills },
                    { name: AboutModuleSubsectionName.DetailsToKnow },
                  ],
                },
                productDetailItems: [
                  ProductDetailItemNames.ShareableCaseStudy,
                  ProductDetailItemNames.RecentlyUpdated,
                  ProductDetailItemNames.LanguagesWithMLInfo,
                  ProductDetailItemNames.InstallationRequirements,
                ],
              } as ProductOverviewModuleConfig
            }
            key={moduleConfig.name}
          />
        );
      case ModuleNames.LearnerIncentive:
        return <LearnerIncentiveModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Syllabus:
        return <ProjectContentModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Testimonials:
        return <TestimonialsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Recommendations:
        return shouldShowXdpChanges ? <ProjectRecommendationsModuleRenderer key={moduleConfig.name} /> : undefined;
      case ModuleNames.ArticlesRecommendations:
        return <ArticlesRecommendationsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Upsell:
        return (
          <UpsellModuleRenderer
            key={moduleConfig.name}
            config={{
              upsellCards: [
                UpsellCardNames.CourseraPlusUpsellCard,
                UpsellCardNames.DegreeUpsellCard,
                UpsellCardNames.EnterpriseUpsellCard,
              ],
            }}
          />
        );
      case ModuleNames.ThinBanner:
        return <ThinBannerModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.PageBanner:
        return <PageBannerModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.Breadcrumbs:
        return <ConsumerBreadcrumbsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Benefits:
        return <BenefitsModuleRenderer key={moduleConfig.name} />;
      default:
        return null;
    }
  });

  return <>{modulesToRender}</>;
};

export default Renderer;
